import { dbOperation } from './database';

// Author storage utilities using the database layer
export const saveAuthor = async (author) => {
  try {
    const authorData = {
      ...author,
      created_at: author.createdAt || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    return await dbOperation.saveAuthor(authorData);
  } catch (error) {
    console.error('Error saving author:', error);
    throw error;
  }
};

export const getAllAuthors = async () => {
  try {
    const authors = await dbOperation.getAllAuthors();
    return Array.isArray(authors) ? authors.sort((a, b) => 
      new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0)
    ) : [];
  } catch (error) {
    console.error('Error getting authors:', error);
    return [];
  }
};

export const getAuthor = async (nameOrId) => {
  try {
    const authors = await getAllAuthors();
    return authors.find(author => 
      author.id === nameOrId || 
      author.name === nameOrId ||
      author.slug === nameOrId
    ) || null;
  } catch (error) {
    console.error('Error getting author:', error);
    return null;
  }
};

export const deleteAuthor = async (id) => {
  try {
    return await dbOperation.deleteAuthor(id);
  } catch (error) {
    console.error('Error deleting author:', error);
    throw error;
  }
};

export const getAuthorBySlug = async (slug) => {
  try {
    const authors = await getAllAuthors();
    return authors.find(author => author.slug === slug) || null;
  } catch (error) {
    console.error('Error getting author by slug:', error);
    return null;
  }
};

export const searchAuthors = async (query) => {
  try {
    const authors = await getAllAuthors();
    const lowercaseQuery = query.toLowerCase();
    return authors.filter(author =>
      author.name?.toLowerCase().includes(lowercaseQuery) ||
      author.title?.toLowerCase().includes(lowercaseQuery) ||
      author.bio?.toLowerCase().includes(lowercaseQuery) ||
      (author.specialties && author.specialties.some(specialty => 
        specialty.toLowerCase().includes(lowercaseQuery)
      ))
    );
  } catch (error) {
    console.error('Error searching authors:', error);
    return [];
  }
};

export const getAuthorStats = async (authorId) => {
  try {
    // This would integrate with blog posts to get stats
    const { getAllBlogPosts } = await import('./blogStorage');
    const posts = await getAllBlogPosts();
    const authorPosts = posts.filter(post => post.author === authorId);
    
    return {
      totalPosts: authorPosts.length,
      publishedPosts: authorPosts.filter(post => post.status === 'published').length,
      featuredPosts: authorPosts.filter(post => post.featured).length,
      totalViews: authorPosts.reduce((sum, post) => sum + (post.views || 0), 0),
      categories: [...new Set(authorPosts.map(post => post.category))],
      lastPublished: authorPosts
        .filter(post => post.status === 'published')
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date || null
    };
  } catch (error) {
    console.error('Error getting author stats:', error);
    return {
      totalPosts: 0,
      publishedPosts: 0,
      featuredPosts: 0,
      totalViews: 0,
      categories: [],
      lastPublished: null
    };
  }
};

// Initialize with sample authors if none exist
export const initializeSampleAuthors = async () => {
  try {
    const existingAuthors = await getAllAuthors();
    if (existingAuthors.length === 0) {
      console.log('Initializing sample authors...');
      
      const sampleAuthors = [
        {
          id: 'sarah-chen',
          name: 'Sarah Chen',
          slug: 'sarah-chen',
          title: 'Senior AI Engineer & Prompt Engineering Specialist',
          bio: 'Sarah is a leading expert in AI-powered development with over 8 years of experience in machine learning and software engineering. She specializes in prompt engineering and has helped numerous companies integrate AI into their development workflows. Sarah holds a PhD in Computer Science from MIT and has published over 20 research papers on AI applications in software development.',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face',
          email: 'sarah.chen@aiximo.com',
          website: 'https://sarahchen.dev',
          location: 'San Francisco, CA',
          yearsExperience: 8,
          articlesCount: 0, // Will be calculated dynamically
          specialties: [
            'AI/ML Engineering',
            'Prompt Engineering',
            'Natural Language Processing',
            'Deep Learning',
            'Python',
            'TensorFlow',
            'OpenAI GPT',
            'LangChain'
          ],
          social: {
            twitter: 'https://twitter.com/sarahchen_ai',
            linkedin: 'https://linkedin.com/in/sarahchen-ai',
            github: 'https://github.com/sarahchen-ai'
          },
          achievements: [
            'PhD in Computer Science, MIT',
            '20+ published research papers',
            'AI Ethics Board Member at Tech for Good',
            'Speaker at 15+ international conferences'
          ],
          languages: ['English', 'Mandarin', 'Python', 'JavaScript'],
          status: 'active',
          featured: true,
          joinDate: '2022-01-15',
          createdAt: '2022-01-15T10:00:00.000Z',
          updatedAt: '2024-12-01T10:00:00.000Z'
        },
        {
          id: 'michael-rodriguez',
          name: 'Michael Rodriguez',
          slug: 'michael-rodriguez',
          title: 'Full-Stack Developer & AI Integration Expert',
          bio: 'Michael is a passionate full-stack developer with extensive experience in modern web technologies and AI integration. He has led development teams at several startups and has a proven track record of delivering scalable applications. Michael is particularly skilled in React, Node.js, and AI-powered backend systems.',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
          email: 'michael.rodriguez@aiximo.com',
          website: 'https://michaeldev.co',
          location: 'Austin, TX',
          yearsExperience: 6,
          articlesCount: 0,
          specialties: [
            'Full-Stack Development',
            'React.js',
            'Node.js',
            'AI Integration',
            'API Development',
            'Database Design',
            'DevOps',
            'Microservices'
          ],
          social: {
            twitter: 'https://twitter.com/mike_codes',
            linkedin: 'https://linkedin.com/in/michael-rodriguez-dev',
            github: 'https://github.com/mike-rodriguez'
          },
          achievements: [
            'BS in Software Engineering, UT Austin',
            'Led 5+ successful startup launches',
            'Certified AWS Solutions Architect',
            'Open source contributor (10k+ GitHub stars)'
          ],
          languages: ['English', 'Spanish', 'JavaScript', 'TypeScript'],
          status: 'active',
          featured: true,
          joinDate: '2022-03-01',
          createdAt: '2022-03-01T10:00:00.000Z',
          updatedAt: '2024-12-01T10:00:00.000Z'
        },
        {
          id: 'jennifer-walsh',
          name: 'Jennifer Walsh',
          slug: 'jennifer-walsh',
          title: 'Agile Coach & AI-Enhanced Project Management Expert',
          bio: 'Jennifer brings over 10 years of experience in project management and agile methodologies. She has pioneered the integration of AI tools into agile workflows, helping teams increase productivity by up to 40%. Jennifer is a certified Scrum Master and has worked with Fortune 500 companies to optimize their development processes.',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face',
          email: 'jennifer.walsh@aiximo.com',
          website: 'https://agileai.expert',
          location: 'Seattle, WA',
          yearsExperience: 10,
          articlesCount: 0,
          specialties: [
            'Agile Methodology',
            'Scrum Master',
            'Project Management',
            'AI-Enhanced Workflows',
            'Team Leadership',
            'Process Optimization',
            'Stakeholder Management',
            'Change Management'
          ],
          social: {
            twitter: 'https://twitter.com/jen_agile_ai',
            linkedin: 'https://linkedin.com/in/jennifer-walsh-pmp',
            github: 'https://github.com/jen-walsh'
          },
          achievements: [
            'MBA in Project Management, Wharton',
            'Certified Scrum Master (CSM)',
            'PMP Certification',
            'Authored "AI in Agile" methodology framework'
          ],
          languages: ['English', 'French', 'German'],
          status: 'active',
          featured: true,
          joinDate: '2022-02-10',
          createdAt: '2022-02-10T10:00:00.000Z',
          updatedAt: '2024-12-01T10:00:00.000Z'
        },
        {
          id: 'david-kim',
          name: 'David Kim',
          slug: 'david-kim',
          title: 'Backend Architect & API Specialist',
          bio: 'David is a seasoned backend architect with deep expertise in building scalable, high-performance systems. He has designed and implemented APIs serving millions of users and has extensive experience with cloud platforms and microservices architecture. David is passionate about clean code and system design principles.',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
          email: 'david.kim@aiximo.com',
          website: 'https://davidkim.tech',
          location: 'New York, NY',
          yearsExperience: 12,
          articlesCount: 0,
          specialties: [
            'Backend Architecture',
            'API Development',
            'Microservices',
            'Database Optimization',
            'Cloud Platforms',
            'System Design',
            'Performance Tuning',
            'Security'
          ],
          social: {
            twitter: 'https://twitter.com/david_backend',
            linkedin: 'https://linkedin.com/in/david-kim-architect',
            github: 'https://github.com/david-kim-arch'
          },
          achievements: [
            'MS in Computer Science, Stanford',
            'Architected systems serving 10M+ users',
            'AWS Certified Solutions Architect Professional',
            'Technical advisor for 3 unicorn startups'
          ],
          languages: ['English', 'Korean', 'Java', 'Go'],
          status: 'active',
          featured: false,
          joinDate: '2022-04-20',
          createdAt: '2022-04-20T10:00:00.000Z',
          updatedAt: '2024-12-01T10:00:00.000Z'
        }
      ];

      for (const author of sampleAuthors) {
        await saveAuthor(author);
        console.log(`✅ Created sample author: ${author.name}`);
      }
      
      console.log(`✅ Initialized ${sampleAuthors.length} sample authors`);
      return true;
    }
    
    console.log(`ℹ️ Found ${existingAuthors.length} existing authors`);
    return false;
  } catch (error) {
    console.error('Error initializing sample authors:', error);
    return false;
  }
};